import { createForm, useCreateFormStore } from '@components/form';
import { IconPersonAdd } from '@components/icons/person-add';
import { IconSend } from '@components/icons/send';
import { Spinner } from '@components/loading-spinner';
import { hideSidebar, Sidebar, useSidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { ExtendedFieldMappingEditorValue } from '@components/text-editors/addons/field-mapping';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import { useCompanyDetails } from 'api/company/hooks';
import { useCreateAndInviteUsers, useRoleOptions, useUsers } from 'api/users/hooks';
import { UserInvite } from 'api/users/requests';

import { PreboardingEmail } from './extensions';

export const useTeamUserSidebar = () => {
  const sidebar = useSidebar(TeamUser, {
    variant: 'CONTENT_HEAVY',
  });
  return sidebar;
};

const Form = createForm<UserInvite>();

export const TeamUser = () => {
  const { t } = useScopedTranslate(locale);

  const createMutation = useCreateAndInviteUsers({ onSuccess: hideSidebar });

  const companyQuery = useCompanyDetails();
  const company = companyQuery.data;

  const formStore = useCreateFormStore();
  const roleOptions = useRoleOptions();

  const { data: users, isLoading } = useUsers();
  if (isLoading || !users) {
    return <Spinner />;
  }

  const existingEmails = users.map((user) => user.email);

  const hasExistingEmailsValidator = (emails?: ExtendedFieldMappingEditorValue[]) => {
    const hasExistingEmails = emails?.some(({ value }) => existingEmails.includes(value));
    return hasExistingEmails ? t('hasExistingEmails') : true;
  };

  return (
    <Sidebar>
      <Sidebar.SimpleHeader
        icon={IconPersonAdd}
        iconColor="elementBlue100"
        title={t('inviteMember', {
          company: company?.name as string,
        })}
      />
      <Sidebar.Content>
        <Form store={formStore} onSubmit={(data) => createMutation.mutate(data)}>
          <Stack space={6} style={{ height: '100%', gap: theme.space[6] }}>
            <Stack orientation="horizontal" justify="space-between">
              <Text font="semibold14" text={t('invite')} />
              <Stack space={4} style={{ maxWidth: pxToRem(391) }}>
                <Text font="regular14" text={t('inviteMessage')} />
                <Form.EmailListField
                  id="emails"
                  label={t('emails')}
                  validators={['required', hasExistingEmailsValidator]}
                  plaintext
                  extensions={[PreboardingEmail.configure({ emails: existingEmails })]}
                />
                <Form.Select
                  id="role"
                  label={t('role')}
                  options={roleOptions}
                  isClearable={false}
                  validators={['required']}
                  defaultValue="editor"
                />
              </Stack>
            </Stack>
            <Sidebar.Separator />
          </Stack>
          <Sidebar.Footer>
            <Form.Actions>
              <Form.Reset text={t('core.cancel')} variant="secondary" onClick={hideSidebar} />
              <Form.Submit
                text={t('sendInvites')}
                iconRight={<IconSend />}
                variant="primary-blue"
              />
            </Form.Actions>
          </Sidebar.Footer>
        </Form>
      </Sidebar.Content>
    </Sidebar>
  );
};

const locale = {
  en: {
    invite: 'Invite',
    inviteMember: 'Invite new member/s to {company}',
    inviteMessage:
      "Invite one or more teammates to collaborate with you in\nJourneys. We'll ask the new members to enter their personal\ndetails when they log in for the first time",
    emails: 'Email addresses',
    sendInvites: 'Send invites',
    hasExistingEmails: 'Email already exists or member already been invited',
    role: 'Role',
    roleMessage: 'Select the role for the invited members',
  },
};
